import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useExpenses, useTimeOffRequests } from './useQueries';

// A draft was never submitted and a cancelled request was taken back; neither is
// anyone's to review, so neither reaches a queue whatever the filter says.
export const REVIEWABLE_STATUSES = new Set(['pending', 'approved', 'rejected']);

export const isPendingRecord = (record) => (
  String(record?.status || '').toLowerCase() === 'pending'
);

// A submitted group is one request to the approver however many receipts are
// in it, so it counts once; only loose expenses count individually.
export const countExpenseRequests = (expenses) => {
  const groupIds = new Set();
  let loose = 0;
  (expenses || []).forEach((expense) => {
    if (expense?.group_id) groupIds.add(String(expense.group_id));
    else loose += 1;
  });
  return groupIds.size + loose;
};

/**
 * What is waiting on the signed-in person to decide.
 *
 * One definition of "reviewable", shared by the approvals inbox and by the
 * badge on the home page — a home badge that counted differently from the queue
 * it points at would be worse than no badge.
 *
 * Both queries are permission-gated, so someone who approves neither fetches
 * neither. React Query caches them, so the inbox reuses whatever the home page
 * already pulled.
 */
export default function usePendingApprovals() {
  const auth = useAuth();

  const currentEmployeeId = String(auth.employee?.id || '');
  const canApproveExpenses = auth.isFinance() || auth.isAdmin() || auth.hasPermission('approve_expenses');
  const canApproveTimeOff = auth.isApprover() || auth.isAdmin() || auth.hasPermission('approve_time_off');
  // View All Expenses sees the expense queue too, without deciding anything.
  const canViewExpenses = canApproveExpenses || auth.hasPermission('view_all_expenses');

  const expensesQuery = useExpenses({ enabled: canViewExpenses });
  const timeOffQuery = useTimeOffRequests({ enabled: canApproveTimeOff });

  // Nobody approves their own request, so it is not in their queue at all — a
  // card that can only ever be refused is noise.
  const isReviewable = (row) => (
    REVIEWABLE_STATUSES.has(String(row?.status || '').toLowerCase())
    && String(row?.employee_id || '') !== currentEmployeeId
  );

  const reviewableExpenses = useMemo(
    () => (canViewExpenses ? (expensesQuery.data || []).filter(isReviewable) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canViewExpenses, currentEmployeeId, expensesQuery.data]
  );

  const reviewableTimeOff = useMemo(
    () => (canApproveTimeOff ? (timeOffQuery.data || []).filter(isReviewable) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canApproveTimeOff, currentEmployeeId, timeOffQuery.data]
  );

  const pendingExpenses = useMemo(
    () => reviewableExpenses.filter(isPendingRecord),
    [reviewableExpenses]
  );

  const pendingTimeOff = useMemo(
    () => reviewableTimeOff.filter(isPendingRecord),
    [reviewableTimeOff]
  );

  // Only what this person can decide is waiting on them.
  const pendingExpenseCount = useMemo(
    () => (canApproveExpenses ? countExpenseRequests(pendingExpenses) : 0),
    [canApproveExpenses, pendingExpenses]
  );

  return {
    canApproveExpenses,
    canApproveTimeOff,
    canViewExpenses,
    currentEmployeeId,
    expensesQuery,
    timeOffQuery,
    reviewableExpenses,
    reviewableTimeOff,
    pendingExpenses,
    pendingTimeOff,
    pendingExpenseCount,
    // What the home page badges: everything this person could act on right now.
    pendingTotal: pendingExpenseCount + (canApproveTimeOff ? pendingTimeOff.length : 0),
  };
}
